import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { inr, day, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useDataTable, TableToolbar, TableFilterRow, TablePager, useRowPick, PickedChip } from '../components/DataTable';
import { SupplierModal, BillModal, BillView, PaymentModal, BILL_STATUS } from '../components/SupplierModals';

// Suppliers, their bills and the payments that settle them — one module, three
// tabs. Bills are payables only: purchases reach the P&L through stock receipts
// and COGS, so nothing here is booked as an expense.

const overdue = (b) => b.dueDate && b.balance > 0.005 && new Date(b.dueDate) < new Date(new Date().toDateString());

const SupplierList = ({ suppliers, loading, reload, canWrite, onPay }) => {
  const [editing, setEditing] = useState(undefined);
  const dt = useDataTable(suppliers, {
    searchPlaceholder: 'Search name, contact, phone, email, GSTIN…',
    searchKeys: ['name', 'contactName', 'phone', 'email', 'gstin'],
    columns: [
      { key: 'name', label: 'Supplier', value: (v) => v.name, placeholder: 'Name' },
      { key: 'contactName', label: 'Contact', value: (v) => v.contactName, placeholder: 'Contact' },
      { key: 'phone', label: 'Phone', value: (v) => v.phone, placeholder: 'Phone' },
      { key: 'terms', label: 'Terms', value: (v) => v.terms, type: 'select' },
      { key: 'status', label: 'Status', value: (v) => (v.isActive ? 'Active' : 'Inactive'), type: 'select' },
    ],
    filters: [
      { key: 'status', label: 'Status', value: (v) => (v.isActive ? 'Active' : 'Inactive') },
      { key: 'due', label: 'Balance', value: (v) => (v.outstanding > 0.005 ? 'Owed' : v.advance > 0.005 ? 'In advance' : 'Settled') },
    ],
  });
  const pick = useRowPick();
  const totalDue = suppliers.reduce((s, v) => s + Math.max(0, Number(v.outstanding) || 0), 0);
  const totalAdv = suppliers.reduce((s, v) => s + (Number(v.advance) || 0), 0);

  return (
    <>
      <p className="muted small">Suppliers provide raw materials and bought products. Purchase orders are raised from <Link to="/procurement">Procurement</Link>; record what they invoice on the Bills tab.</p>
      <TableToolbar dt={dt} right={<>
        <PickedChip pick={pick} />
        <span className="muted small">{inr(totalDue)} payable{totalAdv > 0.005 ? ` · ${inr(totalAdv)} in advance` : ''}</span>{' '}
        {canWrite && <button className="btn btn-primary" onClick={() => setEditing(null)}>+ Add supplier</button>}
      </>} />
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr><th className="freeze-col">Supplier</th><th>Contact</th><th>Phone</th><th>GSTIN</th><th>Terms</th><th className="right">Bills</th><th className="right">Outstanding</th><th>Status</th><th></th></tr>
              <TableFilterRow dt={dt} cells={['name', 'contactName', 'phone', null, 'terms', null, null, 'status', null]} />
            </thead>
            <tbody>
              {dt.rows.map((v) => (
                <tr key={v.id} {...pick.markProps(v.id)}>
                  <td {...pick.cellProps(v.id)}><Link to={`/suppliers/${v.id}`} className="link-strong" onClick={(e) => e.stopPropagation()}>{v.name}</Link></td>
                  <td>{v.contactName || '—'}</td>
                  <td>{v.phone || '—'}</td>
                  <td className="mono small">{v.gstin || '—'}</td>
                  <td>{v.terms || '—'}</td>
                  <td className="right">{v.bills || 0}</td>
                  <td className="right">
                    {v.outstanding > 0.005 ? <strong>{inr(v.outstanding)}</strong>
                      : v.advance > 0.005 ? <span className="muted">{inr(v.advance)} advance</span> : '—'}
                  </td>
                  <td><span className={`badge ${v.isActive ? 'badge-green' : 'badge-gray'}`}>{v.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    {canWrite && v.outstanding > 0.005 && <button className="btn btn-outline btn-sm" onClick={() => onPay(v.id)}>Pay</button>}{' '}
                    {canWrite && <button className="btn btn-outline btn-sm" onClick={() => setEditing(v)}>Edit</button>}
                  </td>
                </tr>
              ))}
              {dt.rows.length === 0 && <tr><td colSpan={9} className="empty">{dt.isFiltered ? 'No suppliers match.' : 'No suppliers yet.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {!loading && <TablePager dt={dt} />}
      {editing !== undefined && <SupplierModal supplier={editing} onClose={() => setEditing(undefined)} onSaved={reload} />}
    </>
  );
};

const BillList = ({ suppliers, canWrite, onPay, reloadSuppliers }) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('due');
  const [creating, setCreating] = useState(false);
  const [editBill, setEditBill] = useState(null);
  const [viewId, setViewId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/suppliers/bills${status === 'all' ? '' : `?status=${status}`}`)
      .then(({ data }) => setBills(data.bills || []))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [status]);
  useEffect(load, [load]);

  const changed = () => { load(); reloadSuppliers(); };

  const dt = useDataTable(bills, {
    searchPlaceholder: 'Search bill no., supplier, note…',
    searchKeys: ['code', 'billNumber', 'note', (b) => b.supplier?.name],
    columns: [
      { key: 'code', label: 'Bill', value: (b) => `${b.code} ${b.billNumber || ''}`, placeholder: 'Bill no.' },
      { key: 'supplier', label: 'Supplier', value: (b) => b.supplier?.name, type: 'select' },
      { key: 'status', label: 'Status', value: (b) => b.status, type: 'select' },
    ],
    filters: [{ key: 'supplier', label: 'Supplier', value: (b) => b.supplier?.name }],
  });
  const pick = useRowPick();
  const sum = (k) => dt.rows.reduce((s, b) => s + (Number(b[k]) || 0), 0);

  return (
    <>
      <TableToolbar dt={dt} right={<>
        <PickedChip pick={pick} />
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 150 }}>
          <option value="due">Due</option><option value="paid">Paid</option>
          <option value="cancelled">Cancelled</option><option value="all">All</option>
        </select>{' '}
        {canWrite && <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New bill</button>}
      </>} />
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr><th className="freeze-col">Bill</th><th>Supplier</th><th>Date</th><th>Due</th><th className="right">Total</th><th className="right">Paid</th><th className="right">Balance</th><th>Status</th><th></th></tr>
              <TableFilterRow dt={dt} cells={['code', 'supplier', null, null, null, null, null, 'status', null]} />
            </thead>
            <tbody>
              {dt.rows.map((b) => (
                <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => setViewId(b.id)} title="Open bill" {...pick.markProps(b.id)}>
                  <td {...pick.cellProps(b.id)}><strong>{b.code}</strong>{b.billNumber ? <span className="muted small"> · {b.billNumber}</span> : null}</td>
                  <td><Link to={`/suppliers/${b.supplierId}`} className="link-strong" onClick={(e) => e.stopPropagation()}>{b.supplier?.name || `#${b.supplierId}`}</Link></td>
                  <td className="muted small">{day(b.billDate)}</td>
                  <td className={overdue(b) ? 'neg small' : 'muted small'}>{b.dueDate ? day(b.dueDate) : '—'}{overdue(b) ? ' · overdue' : ''}</td>
                  <td className="right">{inr(b.total)}</td>
                  <td className="right">{Number(b.paidAmount) ? inr(b.paidAmount) : '—'}</td>
                  <td className="right"><strong>{b.balance > 0.005 ? inr(b.balance) : '—'}</strong></td>
                  <td><span className={`badge ${BILL_STATUS[b.status] || 'badge-gray'}`}>{b.status}</span></td>
                  <td className="right">{canWrite && b.balance > 0.005 && b.status !== 'cancelled'
                    && <button className="btn btn-outline btn-sm" onClick={(e) => { e.stopPropagation(); onPay(b.supplierId); }}>Pay</button>}</td>
                </tr>
              ))}
              {dt.rows.length === 0 && <tr><td colSpan={9} className="empty">{dt.isFiltered ? 'No bills match.' : 'No bills recorded.'}</td></tr>}
            </tbody>
            {dt.rows.length > 0 && (
              <tfoot><tr className="total-row">
                <td colSpan={4}><strong>Total ({dt.rows.length})</strong></td>
                <td className="right">{inr(sum('total'))}</td>
                <td className="right">{inr(sum('paidAmount'))}</td>
                <td className="right"><strong>{inr(sum('balance'))}</strong></td>
                <td colSpan={2}></td>
              </tr></tfoot>
            )}
          </table>
        </div>
      )}
      {!loading && <TablePager dt={dt} />}
      {creating && <BillModal suppliers={suppliers} onClose={() => setCreating(false)} onSaved={changed} />}
      {editBill && <BillModal bill={editBill} suppliers={suppliers} onClose={() => setEditBill(null)} onSaved={changed} />}
      {viewId && <BillView billId={viewId} canWrite={canWrite} onClose={() => setViewId(null)} onChanged={changed} onEdit={setEditBill} />}
    </>
  );
};

const PaymentList = ({ suppliers, canWrite, onPay, reloadSuppliers }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/suppliers/payments').then(({ data }) => setPayments(data.payments || []))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const del = async (p) => {
    if (!window.confirm(`Remove payment ${p.code} of ${inr(p.amount)}?\n\nThe bills it settled go back to outstanding.`)) return;
    try { const { data } = await api.delete(`/suppliers/payments/${p.id}`); toast.success(data.message); load(); reloadSuppliers(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const dt = useDataTable(payments, {
    searchPlaceholder: 'Search reference, supplier, note…',
    searchKeys: ['code', 'reference', 'note', (p) => p.supplier?.name],
    columns: [
      { key: 'code', label: 'Payment', value: (p) => p.code, placeholder: 'Payment no.' },
      { key: 'supplier', label: 'Supplier', value: (p) => p.supplier?.name, type: 'select' },
      { key: 'mode', label: 'Mode', value: (p) => (p.mode || '').toUpperCase(), type: 'select' },
      { key: 'reference', label: 'Reference', value: (p) => p.reference, placeholder: 'UTR / ref' },
    ],
    filters: [
      { key: 'supplier', label: 'Supplier', value: (p) => p.supplier?.name },
      { key: 'mode', label: 'Mode', value: (p) => (p.mode || '').toUpperCase() },
    ],
  });
  const pick = useRowPick();
  const sum = (k) => dt.rows.reduce((s, p) => s + (Number(p[k]) || 0), 0);

  return (
    <>
      <TableToolbar dt={dt} right={<><PickedChip pick={pick} />{canWrite && <button className="btn btn-primary" onClick={() => onPay('')}>+ Record payment</button>}</>} />
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr><th className="freeze-col">Payment</th><th>Supplier</th><th>Date</th><th>Mode</th><th>Reference</th><th>Settled</th><th className="right">Amount</th><th className="right">Advance</th>{canWrite && <th></th>}</tr>
              <TableFilterRow dt={dt} cells={['code', 'supplier', null, 'mode', 'reference', null, null, null, ...(canWrite ? [null] : [])]} />
            </thead>
            <tbody>
              {dt.rows.map((p) => (
                <tr key={p.id} {...pick.markProps(p.id)}>
                  <td {...pick.cellProps(p.id)}><strong>{p.code}</strong></td>
                  <td><Link to={`/suppliers/${p.supplierId}`} className="link-strong">{p.supplier?.name || `#${p.supplierId}`}</Link></td>
                  <td className="muted small">{day(p.paymentDate)}</td>
                  <td>{(p.mode || '').toUpperCase()}</td>
                  <td className="muted small">{p.reference || '—'}</td>
                  <td className="muted small">{(p.allocations || []).length
                    ? p.allocations.map((a) => a.bill?.billNumber || a.bill?.code).filter(Boolean).join(', ')
                    : '—'}</td>
                  <td className="right"><strong>{inr(p.amount)}</strong></td>
                  <td className="right">{p.unallocated > 0.005 ? inr(p.unallocated) : '—'}</td>
                  {canWrite && <td className="right"><button className="btn btn-danger btn-sm" title="Remove payment" onClick={() => del(p)}>✕</button></td>}
                </tr>
              ))}
              {dt.rows.length === 0 && <tr><td colSpan={canWrite ? 9 : 8} className="empty">{dt.isFiltered ? 'No payments match.' : 'No payments recorded.'}</td></tr>}
            </tbody>
            {dt.rows.length > 0 && (
              <tfoot><tr className="total-row">
                <td colSpan={6}><strong>Total ({dt.rows.length})</strong></td>
                <td className="right"><strong>{inr(sum('amount'))}</strong></td>
                <td className="right">{inr(sum('unallocated'))}</td>
                {canWrite && <td></td>}
              </tr></tfoot>
            )}
          </table>
        </div>
      )}
      {!loading && <TablePager dt={dt} />}
    </>
  );
};

const Suppliers = () => {
  const { can } = useAuth();
  const canWrite = can('suppliers:write');
  const [tab, setTab] = useState('suppliers');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);   // supplierId ('' = pick in the dialog)
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/suppliers?all=1').then(({ data }) => setSuppliers(data.suppliers || []))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  // Bumping the key remounts the money tabs so a new bill/payment shows up.
  const refreshAll = () => { load(); setReloadKey((k) => k + 1); };

  return (
    <div>
      <div className="flex gap" style={{ marginBottom: '.8rem' }}>
        <button className={`pill-btn ${tab === 'suppliers' ? 'active' : ''}`} onClick={() => setTab('suppliers')}>Suppliers</button>
        <button className={`pill-btn ${tab === 'bills' ? 'active' : ''}`} onClick={() => setTab('bills')}>Bills</button>
        <button className={`pill-btn ${tab === 'payments' ? 'active' : ''}`} onClick={() => setTab('payments')}>Payments</button>
      </div>

      {tab === 'suppliers' && <SupplierList suppliers={suppliers} loading={loading} reload={load} canWrite={canWrite} onPay={setPaying} />}
      {tab === 'bills' && <BillList key={`b${reloadKey}`} suppliers={suppliers} canWrite={canWrite} onPay={setPaying} reloadSuppliers={load} />}
      {tab === 'payments' && <PaymentList key={`p${reloadKey}`} suppliers={suppliers} canWrite={canWrite} onPay={setPaying} reloadSuppliers={load} />}

      {paying !== null && (
        <PaymentModal suppliers={suppliers} presetSupplierId={paying} onClose={() => setPaying(null)} onSaved={refreshAll} />
      )}
    </div>
  );
};

export default Suppliers;
